/**
 * Geometry of the diagonal marker bands. Pure numbers, no DOM: the slant and
 * the weighting are the whole design here, so they are worth testing on their
 * own rather than by reading a rendered `style` attribute back.
 */

/**
 * An interior band weighs 34 where an edge band weighs 33, so three bands split
 * the cell 33 / 34 / 33 — the odd point goes to the middle. With the corners
 * filled, share and painted area are the same thing, which is why the earlier
 * corrections (0.7, then 0.85) read as pinched: they were fixing a geometry that
 * lost the edge bands' area off the cell.
 */
const INTERIOR_BAND_WEIGHT = 34 / 33;

/**
 * Horizontal shares of the diagonal marker bands, as percentages of the cell.
 * Pure geometry, so the slant and the weighting are testable without a browser.
 */
export function bandSlices(count: number): Array<{ start: number; end: number }> {
  const weights = Array.from({ length: count }, (_, index) =>
    index === 0 || index === count - 1 ? 1 : INTERIOR_BAND_WEIGHT
  );
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let before = 0;
  return weights.map((weight) => {
    const slice = { start: (before / total) * 100, end: ((before + weight) / total) * 100 };
    before += weight;
    return slice;
  });
}

/** How far a band boundary leans, in percent of the cell's width. */
const BAND_SLANT = 14;

/** How far the outermost bands run past the cell, so no corner stays bare. */
const BAND_OVERSHOOT = 50;

/**
 * `clip-path` for one band: the full cell, clipped to its own slice. Only the
 * INTERIOR boundaries lean; the first and last band run past the cell's own
 * edges, or the lean would leave the top-left and bottom-right corners
 * uncovered. Percentages mean the slant holds at every cell size, and no share
 * is lost off the edge — which is what used to hand the middle band of three
 * most of the room.
 */
export function bandClipPath(index: number, count: number): string {
  const { start, end } = bandSlices(count)[index];
  const first = index === 0;
  const last = index === count - 1;
  const leftTop = first ? -BAND_OVERSHOOT : start + BAND_SLANT;
  const leftBottom = first ? -BAND_OVERSHOOT : start - BAND_SLANT;
  const rightTop = last ? 100 + BAND_OVERSHOOT : end + BAND_SLANT;
  const rightBottom = last ? 100 + BAND_OVERSHOOT : end - BAND_SLANT;
  return `polygon(${leftTop}% 0, ${rightTop}% 0, ${rightBottom}% 100%, ${leftBottom}% 100%)`;
}
